import React from 'react';
import { Pagination } from '../../../_lib/components/Pagination';
import { Facets } from '../../../types/Facets';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import ResultListSettings from '../../../components/molecules/ResultListSettings/ResultListSettings';
import { FACETS } from '../../../components/molecules/ResultListSettings/ResultListSettings.enums';
import { SearchResults } from '../../../types/Search.types';
import { DisplayOptions, PageListTeaserPage } from '../PageListDetails.types';
import { PageListTeaserItem } from './PageListTeaserItem';
import { TeaserList, TeaserListHeadline, TeaserListOl } from '../../../_lib/components/TeaserList';
import { SearchFormBase } from '../../Search/partials/SearchFormBase';
import { SearchFormModifier } from '../../Search/partials/SearchFormBase';

type PageListTeaserProps = {
  items?: any;
  displayOptions: DisplayOptions;
  sort?: any;
  facets?: Facets;
  pageItems?: any;
  invisibleHeadline?: boolean;
  headline?: string;
  resetAllFiltersUrl?: {
    url: string;
  };
  searchResults?: SearchResults;
};

/**
 * # PageListTeaser
 *
 * - Component is designed to return page teasers with images, headlines and teaser text.
 * - Additionally, teasers can consist of date, external links and downloads.
 *    They will only be renderd, if they exist in JSON
 * - Modifiers used on ContentHeadline & ContentParagraph to create different font sizes and look.
 * - You can find similar markup and style in the component TopHeadlinesBlock, TopHeadlineItem.
 */
export function PageListTeaser({
  items,
  displayOptions,
  sort = [],
  facets = {},
  pageItems = [],
  invisibleHeadline = true,
  headline,
  resetAllFiltersUrl,
  searchResults,
}: PageListTeaserProps) {
  const { t } = useSonTranslation('PageListDetails');

  const getPages = () => {
    let featuredCounter = 0;
    // @ts-expect-error no typing yet
    return items.reduce((acc, item) => {
      const itemCopy = { ...item };
      if (featuredCounter >= 1) {
        itemCopy.featured = false;
      }

      if (itemCopy.featured) {
        featuredCounter += 1;
      }

      acc.push(itemCopy);

      return acc;
    }, []);
  };

  const showColumn = (columnIdentifier: string) => !displayOptions?.hideColumns?.includes(columnIdentifier);
  const pages = getPages();

  return (
    <TeaserList>
      <TeaserListHeadline invisible={invisibleHeadline}>{headline || t('pageListTeaser.headline')}</TeaserListHeadline>
      {searchResults?.searchForm ? (
        <SearchFormBase
          idPrefix="content"
          modifier={SearchFormModifier.PAGE_LIST}
          maxLength={searchResults.maxLength}
        />
      ) : null}
      {sort.length !== 0 && facets ? (
        <ResultListSettings
          sort={sort}
          facets={facets}
          wantedFacets={[FACETS.FQ_ARCHIVEDATE, FACETS.FQ_PAGE_CATEGORY_TITLES]}
          resetAllFiltersUrl={resetAllFiltersUrl}
        />
      ) : null}
      {pages.length !== 0 ? (
        <TeaserListOl>
          {pages.map((page: PageListTeaserPage) => (
            <PageListTeaserItem
              key={page.pageLink.url}
              page={page}
              showColumn={showColumn}
              displayOptions={displayOptions}
            />
          ))}
        </TeaserListOl>
      ) : null}
      <Pagination pageItems={pageItems} />
    </TeaserList>
  );
}
